/**
 * Payment context helpers.
 *
 * The ONLY purpose of this file is to detect whether Razorpay Checkout is
 * about to open inside a browser tab or inside a standalone / PWA / TWA
 * context. Android Chrome UPI Intent (`intent://…` / `upi://…`) launches
 * are frequently blocked when the site is opened from a home-screen
 * shortcut in `display-mode: standalone`, so we surface that state to
 * the payment dialog and let the user reopen the page in Chrome with a
 * single tap. Nothing here changes pricing, order creation, signature
 * verification, or download logic.
 */

/** Return one of: 'browser' | 'standalone' | 'minimal-ui' | 'fullscreen' | 'twa' */
export function getDisplayMode() {
  if (typeof window === "undefined") return "browser";
  try {
    if (document.referrer && document.referrer.startsWith("android-app://")) {
      return "twa";
    }
    if (window.matchMedia("(display-mode: standalone)").matches) return "standalone";
    if (window.matchMedia("(display-mode: fullscreen)").matches) return "fullscreen";
    if (window.matchMedia("(display-mode: minimal-ui)").matches) return "minimal-ui";
    // iOS Safari legacy: `navigator.standalone`
    if (
      typeof navigator !== "undefined" &&
      // eslint-disable-next-line no-undef
      navigator.standalone === true
    ) {
      return "standalone";
    }
  } catch (_) {}
  return "browser";
}

/**
 * True when Razorpay Checkout is about to open in a context where Android
 * `intent://` UPI-app launches are typically restricted:
 *   • Chrome PWA / Add-to-Home-Screen standalone mode
 *   • Trusted Web Activity (android-app:// referrer)
 * Card, netbanking, wallets, and UPI Collect continue to work fine in
 * these contexts — only external UPI Intent app launches are affected.
 */
export function isRestrictedUpiIntentContext() {
  const mode = getDisplayMode();
  if (mode === "browser") return false;
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  return /Android/i.test(ua);
}
